import React, { useState } from 'react';
import { 
  GraduationCap, Volume2, Star, CheckCircle2, Calendar, 
  BookOpen, Trophy, MessageCircle, Heart, Sparkles, User, Play 
} from 'lucide-react';
import { UserSession } from '../AppShell';

interface StudentPortalDashboardProps {
  currentUser?: UserSession | null;
  onOpenAAC?: () => void;
}

export const StudentPortalDashboard: React.FC<StudentPortalDashboardProps> = ({ currentUser, onOpenAAC }) => {
  const [activeTab, setActiveTab] = useState<'goals' | 'timetable' | 'homework' | 'rewards'>('goals');

  const goals = [
    { title: 'Master 2-Word AAC Sentence Requests', category: 'Communication', progress: 85, status: 'On Track' },
    { title: 'Independent Handwashing & Drying', category: 'Self Care', progress: 90, status: 'Mastered' },
    { title: '/r/ Sound Articulation Pronunciation', category: 'Speech Therapy', progress: 70, status: 'In Progress' }
  ];

  const timetable = [
    { time: '09:00 AM', subject: 'Morning AAC Circle & Greeting', teacher: 'Mrs. Davis', room: 'Room 102' },
    { time: '10:15 AM', subject: 'Individual Speech Pathology Drill', teacher: 'Dr. Sarah (SLP)', room: 'Therapy Suite B' },
    { time: '11:30 AM', subject: 'Occupational Sensory Room Calming', teacher: 'OT Team', room: 'Sensory Room' },
    { time: '01:30 PM', subject: 'Adaptive Music & Art Group', teacher: 'Mr. Harrison', room: 'Activity Hall' }
  ];

  return (
    <div className="py-6 px-4 max-w-7xl mx-auto space-y-6">
      
      {/* Student Portal Header Banner */}
      <div className="bg-gradient-to-r from-blue-900 via-indigo-900 to-purple-900 text-white p-6 rounded-3xl shadow-xl flex flex-col md:flex-row items-center justify-between gap-6 border border-blue-500/30">
        <div className="flex items-center gap-4">
          <div className="w-16 h-16 bg-white/10 border-2 border-amber-400 rounded-2xl flex items-center justify-center text-amber-300 font-black text-2xl shadow-inner">
            🎓
          </div>
          <div>
            <div className="flex items-center gap-2">
              <span className="px-2.5 py-0.5 bg-amber-400/20 text-amber-300 border border-amber-400/30 text-[10px] font-extrabold rounded-full uppercase tracking-wider">
                Student Learning Portal
              </span>
              <span className="text-xs text-blue-200">Grade 4 Special Education</span>
            </div>
            <h1 className="text-2xl md:text-3xl font-black mt-1">
              Welcome back, {currentUser?.name || 'Alexander Vance'}!
            </h1>
            <p className="text-xs text-blue-200 mt-1">
              St. Jude Special Education Center • Roll ID: #STU-2026-884
            </p>
          </div>
        </div>

        <div className="flex items-center gap-3">
          <button
            onClick={onOpenAAC}
            className="px-5 py-3 bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-emerald-600 hover:to-teal-700 text-white font-bold rounded-2xl text-xs shadow-lg transition flex items-center gap-2"
          >
            <Volume2 className="w-4 h-4 text-amber-300" />
            <span>Launch AAC Speech Board</span>
          </button>
        </div>
      </div>

      {/* Portal Tabs Navigation */}
      <div className="flex items-center gap-2 border-b border-slate-200 pb-2">
        <button
          onClick={() => setActiveTab('goals')}
          className={`px-4 py-2 rounded-xl text-xs font-bold transition flex items-center gap-2 ${
            activeTab === 'goals' ? 'bg-blue-600 text-white shadow' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
          }`}
        >
          <GraduationCap className="w-4 h-4" /> My IEP Learning Goals
        </button>

        <button
          onClick={() => setActiveTab('timetable')}
          className={`px-4 py-2 rounded-xl text-xs font-bold transition flex items-center gap-2 ${
            activeTab === 'timetable' ? 'bg-blue-600 text-white shadow' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
          }`}
        >
          <Calendar className="w-4 h-4" /> Daily Schedule
        </button>

        <button
          onClick={() => setActiveTab('rewards')}
          className={`px-4 py-2 rounded-xl text-xs font-bold transition flex items-center gap-2 ${
            activeTab === 'rewards' ? 'bg-blue-600 text-white shadow' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
          }`}
        >
          <Trophy className="w-4 h-4 text-amber-400" /> Token Star Rewards
        </button>
      </div>

      {/* Tab Contents */}
      {activeTab === 'goals' && (
        <div className="space-y-4">
          <h2 className="text-lg font-black text-slate-900">Current Progress on IEP Goals</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {goals.map((g, idx) => (
              <div key={idx} className="bg-white p-5 rounded-2xl border border-slate-200 shadow-sm space-y-3">
                <div className="flex items-center justify-between text-xs">
                  <span className="font-extrabold text-slate-500 uppercase tracking-wider text-[10px]">{g.category}</span>
                  <span className="font-bold px-2 py-0.5 bg-emerald-100 text-emerald-800 rounded text-[10px]">{g.status}</span>
                </div>
                <h3 className="font-bold text-slate-900 text-sm">{g.title}</h3>
                <div className="space-y-1">
                  <div className="flex justify-between text-xs text-slate-500 font-bold">
                    <span>Progress</span>
                    <span className="text-blue-600 font-mono">{g.progress}%</span>
                  </div>
                  <div className="w-full bg-slate-100 h-2 rounded-full overflow-hidden">
                    <div className="bg-gradient-to-r from-blue-500 to-emerald-500 h-full rounded-full" style={{ width: `${g.progress}%` }} />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {activeTab === 'timetable' && (
        <div className="bg-white p-6 rounded-3xl border border-slate-200 shadow-sm space-y-4">
          <h2 className="text-lg font-black text-slate-900">Today's Classroom & Therapy Schedule</h2>
          <div className="space-y-3">
            {timetable.map((t, idx) => (
              <div key={idx} className="p-4 bg-slate-50 rounded-2xl border border-slate-200 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                <div className="flex items-center gap-3">
                  <div className="p-2.5 bg-blue-100 text-blue-800 font-mono font-bold text-xs rounded-xl">
                    {t.time}
                  </div>
                  <div>
                    <div className="font-bold text-slate-900 text-sm">{t.subject}</div>
                    <div className="text-xs text-slate-500">Educator: {t.teacher}</div>
                  </div>
                </div>
                <span className="text-xs font-bold text-indigo-600 bg-indigo-50 px-3 py-1 rounded-xl self-start sm:self-auto">
                  {t.room}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {activeTab === 'rewards' && (
        <div className="bg-gradient-to-r from-amber-50 to-orange-50 border border-amber-200 p-8 rounded-3xl text-center space-y-4">
          <div className="w-16 h-16 bg-amber-400 text-slate-950 rounded-full flex items-center justify-center mx-auto shadow-lg text-3xl font-black">
            ⭐
          </div>
          <h2 className="text-2xl font-black text-slate-900">Token Star Reward Board</h2>
          <p className="text-xs text-slate-600 max-w-md mx-auto">
            You have earned 14 Token Stars this week for great participation and completed speech therapy drills!
          </p>
          <div className="flex items-center justify-center gap-2 pt-2">
            {[...Array(5)].map((_, i) => (
              <span key={i} className="text-3xl text-amber-400 drop-shadow">⭐</span>
            ))}
          </div>
        </div>
      )}

    </div>
  );
};
